import { zodResolver } from '@hookform/resolvers/zod';
import { useFormSchema } from '@modules/dialogs/hooks/use-form-schema';
import type { MessageSendFormValues } from '@modules/dialogs/lib/types';
import { Button, Form, Input } from 'antd';
import { type FC, type KeyboardEvent } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './chat-input.module.scss';
import { Send } from '@shared/assets/icons/components';
import { useSendMessage } from '@modules/dialogs/hooks/use-send-message';

interface ChatInputProps {
  clientId: number;
}

export const ChatInput: FC<ChatInputProps> = ({ clientId }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'messages' });
  const { sendDialogMessage, isLoading } = useSendMessage();

  const updateMessageFormSchema = useFormSchema();

  const {
    handleSubmit,
    control,
    clearErrors,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<MessageSendFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(updateMessageFormSchema),
    defaultValues: {
      message: '',
    },
  });

  const messageValue = useWatch({
    control,
    name: 'message',
  });

  const canSubmit = Boolean(messageValue?.trim()) && isValid && !isSubmitting && !isLoading;

  const handleSendMessage = async (values: MessageSendFormValues) => {
    if (!values.message.trim()) {
      return;
    }

    await sendDialogMessage(clientId, values.message);
    clearErrors();
    reset();
  };

  const submitForm = handleSubmit(handleSendMessage);

  const handlePressEnter = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.shiftKey || e.nativeEvent.isComposing) {
      return;
    }

    e.preventDefault();

    if (!canSubmit) {
      return;
    }

    void submitForm();
  };

  return (
    <Form className={styles.form} onFinish={submitForm}>
      <Form.Item name="correctedMessage" validateStatus={errors.message ? 'error' : ''}>
        <Controller
          control={control}
          name="message"
          render={({ field }) => (
            <Input.TextArea
              {...field}
              className={styles.textarea}
              placeholder={t('textareaPlaceholder')}
              onPressEnter={handlePressEnter}
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          )}
        />
      </Form.Item>

      <Button
        icon={<Send />}
        htmlType="submit"
        loading={isSubmitting || isLoading}
        disabled={!canSubmit}
        className={styles.submit}
      />
    </Form>
  );
};
